import React from 'react';
import styled, { css } from 'styled-components';
import { theme } from '../../styles/theme';

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  fullWidth?: boolean;
  variant?: 'default' | 'search';
}

const InputContainer = styled.div<{ fullWidth?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.sm};
  width: ${({ fullWidth }) => (fullWidth ? '100%' : 'auto')};
`;

const Label = styled.label`
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
  color: ${theme.colors.text.secondary};
`;

const InputWrapper = styled.div.withConfig({
  shouldForwardProp: (prop) => !['hasError', 'variant'].includes(prop),
})<{ hasError?: boolean; variant?: string }>`
  position: relative;
  display: flex;
  align-items: center;
  background: ${theme.colors.background.tertiary};
  border: 2px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.large};
  transition: all ${theme.transitions.fast};
  
  ${({ variant }) => variant === 'search' && css`
    border-radius: ${theme.borderRadius.full};
    background: ${theme.colors.background.card};
  `}
  
  &:focus-within {
    border-color: ${theme.colors.primary};
    box-shadow: ${theme.shadows.glow};
  }
  
  ${({ hasError }) => hasError && css`
    border-color: ${theme.colors.error};
  `}
`;

const StyledInput = styled.input.withConfig({
  shouldForwardProp: (prop) => !['hasLeftIcon', 'hasRightIcon'].includes(prop),
})<{ hasLeftIcon?: boolean; hasRightIcon?: boolean }>`
  flex: 1;
  padding: ${theme.spacing.md};
  background: transparent;
  border: none;
  outline: none;
  color: ${theme.colors.text.primary};
  font-size: ${theme.typography.fontSize.md};
  font-family: ${theme.typography.fontFamily.primary};
  
  ${({ hasLeftIcon }) => hasLeftIcon && css`
    padding-left: ${theme.spacing.sm};
  `}
  
  ${({ hasRightIcon }) => hasRightIcon && css`
    padding-right: ${theme.spacing.sm};
  `}
  
  &::placeholder {
    color: ${theme.colors.text.tertiary};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const IconWrapper = styled.div<{ position: 'left' | 'right' }>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing.md};
  color: ${theme.colors.text.tertiary};
  
  ${({ position }) => position === 'left' && css`
    padding-right: ${theme.spacing.sm};
  `}
  
  ${({ position }) => position === 'right' && css`
    padding-left: ${theme.spacing.sm};
  `}
`;

const ErrorMessage = styled.span`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.error};
  margin-top: ${theme.spacing.xs};
`;

export const Input: React.FC<InputProps> = ({
  label,
  error,
  leftIcon,
  rightIcon,
  fullWidth,
  variant = 'default',
  ...props
}) => {
  return (
    <InputContainer fullWidth={fullWidth}>
      {label && <Label>{label}</Label>}
      <InputWrapper hasError={!!error} variant={variant}>
        {leftIcon && <IconWrapper position="left">{leftIcon}</IconWrapper>}
        <StyledInput
          hasLeftIcon={!!leftIcon}
          hasRightIcon={!!rightIcon}
          {...props}
        />
        {rightIcon && <IconWrapper position="right">{rightIcon}</IconWrapper>}
      </InputWrapper>
      {error && <ErrorMessage>{error}</ErrorMessage>}
    </InputContainer>
  );
}; 